import React, { useState } from 'react';
import { useData } from '../contexts/DataContext';
import { useAuth } from '../contexts/AuthContext';
import { Plus, Edit, Trash2, Heart, Calendar, Shield } from 'lucide-react';
import PetCard from '../components/PetCard';
import ProfilePictureUpload from '../components/ProfilePictureUpload';
import DataFetchState from '../components/ui/DataFetchState';
import AnimatedEmptyState from '../components/ui/AnimatedEmptyState';

const PetProfiles = () => {
  const { pets, addPet, updatePet, deletePet, userDataLoading, userDataError, refreshUserData } = useData();
  const { user } = useAuth();
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingPet, setEditingPet] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    species: 'Dog',
    breed: '',
    age: '',
    ageMonths: '',
    weightKg: '',
    medications: [],
    vaccinationRecords: [],
    notes: '',
    photos: []
  });

  // Get user ID (handle both uid and id for compatibility)
  const userId = user?.uid || user?.id;
  const userPets = pets.filter(pet => pet.ownerId === userId);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const petData = {
      ...formData,
      ownerId: userId,
      age: parseInt(formData.age) || 0,
      ageMonths: parseInt(formData.ageMonths) || 0,
      weightKg: parseFloat(formData.weightKg) || 0
    };

    try {
      if (editingPet) {
        await updatePet(editingPet.id, petData);
        setEditingPet(null);
      } else {
        await addPet(petData);
      }
    } catch (error) {
      console.error('Error saving pet:', error);
      alert('Error saving pet. Please try again.');
      return;
    }
    
    // Reset form
    setFormData({
      name: '',
      species: 'Dog',
      breed: '',
      age: '',
      ageMonths: '',
      weightKg: '',
      medications: [],
      vaccinationRecords: [],
      notes: '',
      photos: []
    });
    setShowAddForm(false);
  };

  const handleEdit = (pet) => {
    setEditingPet(pet);
    setFormData({
      name: pet.name,
      species: pet.species,
      breed: pet.breed,
      age: pet.age?.toString() || '',
      ageMonths: pet.ageMonths?.toString() || '',
      weightKg: pet.weightKg?.toString() || '',
      medications: pet.medications || [],
      vaccinationRecords: pet.vaccinationRecords || [],
      notes: pet.notes || '',
      photos: pet.photos || []
    });
    setShowAddForm(true);
  };

  const handleDelete = (petId) => {
    if (window.confirm('Are you sure you want to delete this pet profile?')) {
      deletePet(petId);
    }
  };

  return (
    <div className="pf-page-wrap pb-32 lg:pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold text-[var(--pf-ink)] mb-2">My Pets</h1>
            <p className="text-gray-600">
              Manage your pet profiles and health information
            </p>
          </div>
          <button
            onClick={() => setShowAddForm(true)}
            className="btn-primary flex items-center gap-2 shadow-xl"
          >
            <Plus className="w-5 h-5" />
            <span>Add New Pet</span>
          </button>
        </div>

        {/* Add/Edit Form */}
        {showAddForm && (
          <div className="card mb-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">
                {editingPet ? 'Edit Pet Profile' : 'Add New Pet'}
              </h2>
              <button
                onClick={() => {
                  setShowAddForm(false);
                  setEditingPet(null);
                  setFormData({
                    name: '',
                    species: 'Dog',
                    breed: '',
                    age: '',
                    weightKg: '',
                    medications: [],
                    vaccinationRecords: [],
                    notes: '',
                    photos: []
                  });
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Pet Picture Upload */}
              <ProfilePictureUpload
                currentAvatar={formData.photos?.[0] || 'https://api.dicebear.com/7.x/shapes/svg?seed=pet'}
                onUploadComplete={(imageUrl) => {
                  setFormData({
                    ...formData,
                    photos: [imageUrl, ...(formData.photos || []).slice(1)]
                  });
                }}
                size="large"
                label="Pet Picture"
                userId={user?.uid || user?.id}
                petId={formData.id || 'new'}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-2">
                    Pet Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="input-field"
                    placeholder="e.g., Buddy, Whiskers"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-2">
                    Species *
                  </label>
                  <select
                    value={formData.species}
                    onChange={(e) => setFormData({...formData, species: e.target.value})}
                    className="input-field"
                    required
                  >
                    <option value="Dog">🐕 Dog</option>
                    <option value="Cat">🐈 Cat</option>
                    <option value="Bird">🦜 Bird</option>
                    <option value="Other">🐾 Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-2">
                    Breed
                  </label>
                  <input
                    type="text"
                    value={formData.breed}
                    onChange={(e) => setFormData({...formData, breed: e.target.value})}
                    className="input-field"
                    placeholder="e.g., Golden Retriever, Persian"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-2">
                    Age *
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Years</label>
                      <input
                        type="number"
                        value={formData.age}
                        onChange={(e) => setFormData({...formData, age: e.target.value})}
                        className="input-field"
                        placeholder="0"
                        min="0"
                        max="30"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Months</label>
                      <input
                        type="number"
                        value={formData.ageMonths}
                        onChange={(e) => setFormData({...formData, ageMonths: e.target.value})}
                        className="input-field"
                        placeholder="0"
                        min="0"
                        max="11"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-2">
                    Weight (kg) *
                  </label>
                  <input
                    type="number"
                    value={formData.weightKg}
                    onChange={(e) => setFormData({...formData, weightKg: e.target.value})}
                    className="input-field"
                    placeholder="e.g., 5.5"
                    min="0"
                    step="0.1"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-2">
                  Special Notes / Care Instructions
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  className="input-field h-32"
                  placeholder="Any special care instructions, dietary restrictions, behavior notes, or preferences..."
                  rows="4"
                />
              </div>

              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddForm(false);
                    setEditingPet(null);
                  }}
                  className="btn-outline flex-1"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary flex-1 flex items-center justify-center gap-2"
                >
                  {editingPet ? (
                    <>
                      <Edit className="w-5 h-5" />
                      Update Pet Profile
                    </>
                  ) : (
                    <>
                      <Plus className="w-5 h-5" />
                      Add Pet to My Pets
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Pets Grid */}
        <DataFetchState
          loading={userDataLoading}
          error={userDataError}
          onRetry={refreshUserData}
          loadingLabel="Loading your pets"
          errorTitle="Couldn't load pet profiles"
          compact
        >
        {userPets.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {userPets.map((pet) => (
              <div key={pet.id} className="card relative">
                <div className="flex gap-4">
                  <img
                    src={pet.photos[0] || 'https://images.unsplash.com/photo-1552053831-71594a27632d?w=400&h=300&fit=crop'}
                    alt={pet.name}
                    className="w-20 h-20 rounded-lg object-cover"
                  />
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">{pet.name}</h3>
                        <p className="text-sm text-gray-600">
                          {pet.breed} • {pet.age || 0} {pet.age === 1 ? 'year' : 'years'}
                          {pet.ageMonths ? ` ${pet.ageMonths} ${pet.ageMonths === 1 ? 'month' : 'months'}` : ''} old
                        </p>
                      </div>
                      <div className="flex items-center gap-1">
                        <Heart className="w-4 h-4 text-pink-500" />
                        <span className="text-sm text-gray-600">{pet.weightKg}kg</span>
                      </div>
                    </div>
                    
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                      {pet.notes}
                    </p>
                    
                    <div className="flex items-center gap-2 mb-3">
                      <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-[var(--pf-accent-soft)] text-[var(--pf-accent)]">{pet.species}</span>
                      {pet.medications.length > 0 && (
                        <span className="badge badge-warning">Medication</span>
                      )}
                      {pet.vaccinationRecords.length > 0 && (
                        <span className="badge badge-success">Vaccinated</span>
                      )}
                    </div>
                    
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEdit(pet)}
                        className="btn-outline text-sm px-3 py-1 flex items-center gap-1"
                      >
                        <Edit className="w-3 h-3" />
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(pet.id)}
                        className="btn-outline text-sm px-3 py-1 text-red-600 border-red-300 hover:bg-red-50 flex items-center gap-1"
                      >
                        <Trash2 className="w-3 h-3" />
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <AnimatedEmptyState
            icon="loader-running-dog"
            title="No pets added yet"
            description="Add your first pet profile to start booking care services and connect with local caregivers"
            action={
              <button
                type="button"
                onClick={() => setShowAddForm(true)}
                className="pf-btn-primary inline-flex items-center gap-2"
              >
                <Plus className="w-5 h-5" />
                Add your first pet
              </button>
            }
          />
        )}
        </DataFetchState>

        {/* Tips */}
        {userPets.length > 0 && (
          <div className="mt-12">
            <div className="pf-organic-card bg-[var(--pf-accent-soft)] border-[color-mix(in_srgb,var(--pf-accent)_20%,var(--pf-border))]">
              <h3 className="text-lg font-semibold text-[var(--pf-ink)] mb-3">
                Tips for better pet care
              </h3>
              <ul className="space-y-2 text-sm text-[var(--pf-ink-muted)]">
                <li>Keep vaccination records up to date for better caregiver matching</li>
                <li>Include detailed medication schedules and special care instructions</li>
                <li>Add multiple photos showing your pet&apos;s personality and size</li>
                <li>Update your pet&apos;s profile regularly as they grow and change</li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PetProfiles;
